import type { Condition, FieldCondition } from 'features/charts-v2/types/yogiql'
import {
  DEFAULT_TRAILING_DAYS,
  TimeFrame,
} from 'features/dashboards-v2-1/features/filter-header/constants'
import type { DashboardFilterStateV2 } from 'features/dashboards-v2-1/types/filters-v2'

// A calendar resolution's date-math unit: its most recent complete period.
const PERIOD_UNIT: Partial<Record<string, string>> = {
  [TimeFrame.WEEK]: 'w',
  [TimeFrame.MONTH]: 'M',
  [TimeFrame.QUARTER]: 'Q',
  [TimeFrame.HALF]: 'H',
  [TimeFrame.YEAR]: 'y',
}

const PERIOD_FRAME = Object.fromEntries(
  Object.entries(PERIOD_UNIT).map(([frame, unit]) => [unit, frame]),
) as Record<string, string>

// The trailing presets the picker builds by calendar subtraction but keys by
// their rounded day count; the backend's `_TRAILING_PERIODS` is the other half.
const TRAILING_PERIODS: Partial<Record<number, string>> = {
  91: '3M',
  183: '6M',
  365: '1y',
  730: '2y',
}

const TRAILING_DAYS = Object.fromEntries(
  Object.entries(TRAILING_PERIODS).map(([days, offset]) => [
    offset,
    Number(days),
  ]),
) as Record<string, number>

const TRAILING = /^now-([1-9]\d*)d\/d$/
const TRAILING_PERIOD = /^now-([1-9]\d*[My])\/d$/
const PERIOD = /^now-1([wMQHy])\/\1$/

/** How the page holds a date: "Most recent" on (`isCurrent`) with its
 *  resolution, or a range (`null`: none of its own). */
export type PageDate = {
  isCurrent: boolean
  timeResolution?: string
  trailingDaysCount?: number
  dateRange: DashboardFilterStateV2['dateRange']
}

// YogiQL instants: the stored second, no millis; 0 is an open bound.
const toInstant = (epochSeconds: number): string | undefined =>
  epochSeconds
    ? new Date(epochSeconds * 1000).toISOString().replace(/\.\d{3}Z$/, 'Z')
    : undefined

const fromInstant = (value: unknown): number =>
  typeof value === 'string' ? Math.round(Date.parse(value) / 1000) : 0

const between = (start: string, end: string): FieldCondition => ({
  field: 'date',
  op: 'between',
  values: [start, end],
})

/** A rolling window as date math, day-rounded or coarser so it matches the
 *  picker's midnight-to-end-of-day snapping; null for a resolution the page
 *  does not offer. */
const rolling = (
  timeResolution: string | undefined,
  trailingDaysCount: number | undefined,
): FieldCondition | null => {
  const frame = timeResolution || TimeFrame.TRAILING
  if (frame === TimeFrame.TRAILING) {
    const days = trailingDaysCount ?? DEFAULT_TRAILING_DAYS
    return between(`now-${TRAILING_PERIODS[days] ?? `${days}d`}/d`, 'now/d')
  }
  if (frame === TimeFrame.YTD) return between('now/y', 'now/d')
  const unit = PERIOD_UNIT[frame]
  return unit ? between(`now-1${unit}/${unit}`, `now-1${unit}/${unit}`) : null
}

/** Whether a condition is a date window: one on `date` bounding a range. */
export const isDateWindow = (
  condition: Condition,
): condition is FieldCondition =>
  'field' in condition &&
  condition.field === 'date' &&
  ['between', 'gte', 'lte'].includes(condition.op)

/** A date as the condition the YogiQL routes hold it in: date math while
 *  "Most recent" is on, else the range it holds, else none (`null`: it
 *  follows its parent). */
export function toDateWindow(date: PageDate): FieldCondition | null {
  if (date.isCurrent) {
    const window = rolling(date.timeResolution, date.trailingDaysCount)
    if (window) return window
  }
  if (!date.dateRange) return null
  const start = toInstant(date.dateRange.startDate)
  const end = toInstant(date.dateRange.endDate)
  if (start && end) return between(start, end)
  if (start) return { field: 'date', op: 'gte', value: start }
  if (end) return { field: 'date', op: 'lte', value: end }
  return null
}

// The epoch reads back as an open bound (0), so this round-trips as all time.
const ALL_TIME_WINDOW: FieldCondition = {
  field: 'date',
  op: 'gte',
  value: '1970-01-01T00:00:00Z',
}

/** A tile's own window, as `toDateWindow`; a range open at both ends is still
 *  one (all time), since a tile with no window follows the dashboard. */
export function tileDateWindow(date: PageDate): FieldCondition | null {
  return toDateWindow(date) ?? (date.dateRange ? ALL_TIME_WINDOW : null)
}

/** A date window as the page holds it; a rolling one has no range of its own
 *  until the page resolves it against the hub's date windows. Null for date
 *  math the date control does not offer. */
export function fromDateWindow(windows: FieldCondition[]): PageDate | null {
  const [first] = windows
  const [start, end] = first?.values ?? []
  if (
    windows.length === 1 &&
    typeof start === 'string' &&
    start.startsWith('now')
  ) {
    const trailingPeriod = TRAILING_PERIOD.exec(start)?.[1]
    const days = trailingPeriod
      ? TRAILING_DAYS[trailingPeriod]
      : Number(TRAILING.exec(start)?.[1] ?? NaN)
    if (days && end === 'now/d') {
      return {
        isCurrent: true,
        timeResolution: TimeFrame.TRAILING,
        trailingDaysCount: days,
        dateRange: null,
      }
    }
    if (start === 'now/y' && end === 'now/d') {
      return {
        isCurrent: true,
        timeResolution: TimeFrame.YTD,
        dateRange: null,
      }
    }
    const period = PERIOD.exec(start)
    if (period && end === start) {
      return {
        isCurrent: true,
        timeResolution: PERIOD_FRAME[period[1]!],
        dateRange: null,
      }
    }
    return null
  }
  const range = { startDate: 0, endDate: 0 }
  for (const window of windows) {
    if (window.op === 'between') {
      range.startDate = fromInstant(window.values?.[0])
      range.endDate = fromInstant(window.values?.[1])
    } else if (window.op === 'gte') range.startDate = fromInstant(window.value)
    else range.endDate = fromInstant(window.value)
  }
  if (Object.values(range).some(Number.isNaN)) return null
  return { isCurrent: false, dateRange: range }
}

/** A dashboard's own window; null for a range open at both ends (all time).
 *  A legacy one with "Most recent" off and no range rolls over the default
 *  trailing window: its tiles used to fall back to each panel's saved window
 *  instead. */
export function dashboardDateWindow(date: PageDate): FieldCondition | null {
  const window = toDateWindow(date)
  if (window || date.dateRange) return window
  return rolling(TimeFrame.TRAILING, undefined)
}
